import { ButtonSecondary, ButtonTertiary, IconButton, RadioButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Divider } from "components/Divider"
import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectColorFinderAnomalyEnabled,
    selectColorFinderFlightId,
    selectColorFinderFootageIsZoomed,
    selectColorFinderMapSettings,
    selectColorFinderSourceEnabled,
    selectColorFinderTool,
} from "./redux/selectors"
import {
    closeColorFinder,
    resetColorFinderFootageZoom,
    setColorFinderAnomalyEnabled,
    setColorFinderMapSettings,
    setColorFinderSourceEnabled,
    setColorFinderTool,
    setColorFinderView,
} from "./redux/slice"
import { useLocalStorageValue, setLocalStorageValue } from "tools/store/localStorageRegistry"
import { SegmentedControl } from "panels/content/top-bar/TODO-put-in-package/SegmentedControl"
import classNames from "classnames"
import { Popup } from "@nova/component-popup"
import { Checkbox } from "@nova/component-checkbox"
import { useMapBackground } from "features/map/control/useMapStyles"
import {
    flightHasResolutions,
    isVideoFlight,
    isVideoFrameMetadataGeoValid,
    useColorFinderMetadata,
} from "./ColorFinderQueries"
import { PropsWithChildren, useLayoutEffect, useMemo, useState } from "react"
import { setInspectorPanelState } from "features/popups/slice"
import { Tooltip } from "./Tooltip"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import { ToggleSwitch } from "@nova/component-toggle-switch"

const options = ["default", "flipped", "fullscreen"] as const
const qualityWarningKey = "VCF-QLTY-WRNG"

const ColorFinderTopBar = () => {
    const dispatch = useAppDispatch()

    const flightId = useAppSelector(selectColorFinderFlightId)
    const flight = useAppSelector(selectColorFinderFlightById(flightId))
    const activeTool = useAppSelector(selectColorFinderTool)
    const isZoomed = useAppSelector(selectColorFinderFootageIsZoomed)
    const quality = useLocalStorageValue("VCF-QLTY-PRFRNC")

    const [bannerHasBeenShown, setBannerHasBeenShown] = useState(false)

    const [showQualityWarning, setShowQualityWarning] = useState(
        localStorage.getItem(qualityWarningKey) !== "true" &&
            isVideoFlight(flight) &&
            !!flight?.job?.resolutions.length &&
            quality !== "high"
    )

    const { data: metadata, isLoading } = useColorFinderMetadata(flight)

    const isPointPlaceable = useMemo(() => {
        return !isLoading && metadata && metadata.every(isVideoFrameMetadataGeoValid)
    }, [isLoading, metadata])

    useLayoutEffect(() => {
        if (!isPointPlaceable && !isLoading && flight && !bannerHasBeenShown) {
            dispatch(
                setGlobalNotificationContext({
                    message: "No SRT data found. Some features are disabled.",
                    status: GlobalNotificationStatus.warning,
                    timeoutInMs: 3000,
                })
            )
            setBannerHasBeenShown(true)
        }
    }, [bannerHasBeenShown, dispatch, flight, isLoading, isPointPlaceable])

    const handleClose = () => {
        dispatch(closeColorFinder())
        dispatch(setInspectorPanelState("active"))
    }

    const handleToolClick = (tool: typeof activeTool) => () => {
        dispatch(setColorFinderTool(tool === activeTool ? "" : tool))
    }

    if (!flight) {
        handleClose()
        return null
    }

    const TooltipWrapper = ({ children }: PropsWithChildren) => {
        if (isPointPlaceable) return <div>{children}</div>
        return (
            <div>
                <Tooltip message="Unavailable - SRT file required">{children}</Tooltip>
            </div>
        )
    }

    const closeQualityWarning = () => {
        if (showQualityWarning) {
            setShowQualityWarning(false)
            localStorage.setItem(qualityWarningKey, "true")
        }
    }

    return (
        <div className="text-gray-100 p-3 pr-2.5 pl-5 grid grid-cols-[1fr_auto_1fr] items-center">
            <div className="flex gap-5 items-center h-full justify-self-start">
                <IconButton onClick={handleClose}>
                    <Icons.SmallIcon Icon={Icons.XClose} />
                </IconButton>
                <Divider vertical />
                <div className="flex items-center gap-2">
                    <Icons.ColorFinder /> <h4 className="text-white">{flight.name}</h4>
                </div>
            </div>

            <div className="justify-self-center">
                <TooltipWrapper>
                    <IconButton
                        onClick={handleToolClick("point")}
                        disabled={!isPointPlaceable}
                        className={classNames(
                            "p-1.5",
                            activeTool === "point" && "bg-blue-500 text-gray-800 rounded-md"
                        )}
                    >
                        <Icons.PinDrop />
                    </IconButton>
                </TooltipWrapper>
            </div>

            <div className="flex gap-2 items-center justify-self-end">
                <ButtonTertiary
                    className={classNames(
                        "flex-shrink-0 !px-3 mr-6 !border-none",
                        !isZoomed && "invisible"
                    )}
                    RightIcon={Icons.FitScreen}
                    title="Reset Zoom"
                    onClick={() => dispatch(resetColorFinderFootageZoom())}
                />
                <small className="text-gray-200 mr-1">View</small>
                <SegmentedControl
                    defaultSelectedIndex={0}
                    onChange={(index) => {
                        dispatch(setColorFinderView(options[index]))
                    }}
                    segments={[
                        <IconButton className="px-2" key={1}>
                            <Icons.LgMap />
                        </IconButton>,
                        <IconButton className="px-2" key={2}>
                            <Icons.SmMap />
                        </IconButton>,
                        <IconButton className="px-2" key={3}>
                            <Icons.Fullscreen />
                        </IconButton>,
                    ]}
                />
                <div className="relative">
                    <Popup
                        position="bottom-left"
                        content={
                            <Popup.PopupModal className="!p-0">
                                <SettingsPanel flight={flight} />
                            </Popup.PopupModal>
                        }
                    >
                        <ButtonSecondary
                            LeftIcon={Icons.SettingsCog}
                            onClick={closeQualityWarning}
                            className="flex-shrink-0 !px-3"
                            title="Settings"
                        />
                    </Popup>
                    {showQualityWarning && (
                        <>
                            <div className="absolute top-full right-0 z-10">
                                <div className="flex items-center gap-1 bg-[#14232D] border border-blue-700 rounded-lg py-1 px-2 mt-2">
                                    <small className="whitespace-nowrap">
                                        Raise the source quality to improve detail.
                                    </small>
                                    <IconButton
                                        onClick={closeQualityWarning}
                                        className="text-gray-300"
                                    >
                                        <Icons.ExtraSmallIcon Icon={Icons.XClose} />
                                    </IconButton>
                                </div>
                            </div>
                            <div className="absolute rotate-45 aspect-square w-2 bg-[#14232D] border-l border-t border-blue-700 mt-1 right-1/2 z-10" />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

const SettingsPanel = ({ flight }: { flight: ImageColorFinderFlight | VideoColorFinderFlight }) => {
    const dispatch = useAppDispatch()
    const { mapBackgrounds, isLight } = useMapBackground()
    const { crosshair, fieldOfView, flightPath } = useAppSelector(selectColorFinderMapSettings)
    const quality = useLocalStorageValue("VCF-QLTY-PRFRNC")
    const sourceEnabled = useAppSelector(selectColorFinderSourceEnabled)
    const anomalyEnabled = useAppSelector(selectColorFinderAnomalyEnabled)

    const isFlightHasResoltions = flightHasResolutions(flight)
    const isImageFlight = !isVideoFlight(flight)

    return (
        <div className="flex flex-col gap-2 w-64 divide-y divide-white-200 py-4">
            <div className="flex flex-col gap-2 px-4">
                <small className="text-gray-300">FOOTAGE SETTINGS</small>
                <div className="flex justify-between items-center min-h-[32px]">
                    <h5>Source</h5>
                    <ToggleSwitch
                        isToggled={sourceEnabled}
                        setIsToggled={(v) => dispatch(setColorFinderSourceEnabled(v))}
                    />
                </div>
                {isFlightHasResoltions && (
                    <>
                        <div className="flex gap-3 pl-2 items-center">
                            <RadioButton
                                isSelected={quality === "low"}
                                onClick={() => {
                                    setLocalStorageValue("VCF-QLTY-PRFRNC", "low")
                                }}
                            />
                            <h5>Low</h5>
                        </div>
                        {!isImageFlight && (
                            <div className="flex gap-3 pl-2 items-center">
                                <RadioButton
                                    isSelected={quality === "medium"}
                                    onClick={() => {
                                        setLocalStorageValue("VCF-QLTY-PRFRNC", "medium")
                                    }}
                                />
                                <h5>Medium</h5>
                            </div>
                        )}

                        <div className="flex gap-3 pl-2 items-center">
                            <RadioButton
                                isSelected={quality === "high"}
                                onClick={() => {
                                    setLocalStorageValue("VCF-QLTY-PRFRNC", "high")
                                }}
                            />
                            <h5>High</h5>
                        </div>
                    </>
                )}
            </div>

            <div className="flex justify-between items-center pt-2 min-h-[32px] px-4">
                <h5>Anomaly Filter</h5>
                <ToggleSwitch
                    isToggled={anomalyEnabled}
                    setIsToggled={(v) => dispatch(setColorFinderAnomalyEnabled(v))}
                />
            </div>

            <div className="flex flex-col px-4">
                <small className="text-gray-300 py-2 pt-4">MAP SETTINGS</small>

                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-3 py-1.5">
                        <div className="flex gap-3 items-center">
                            <Checkbox
                                isChecked={crosshair}
                                onClick={() =>
                                    dispatch(setColorFinderMapSettings({ crosshair: !crosshair }))
                                }
                            />
                            <h5>Crosshair</h5>
                        </div>

                        <div className="flex gap-3 items-center">
                            <Checkbox
                                isChecked={fieldOfView}
                                onClick={() =>
                                    dispatch(
                                        setColorFinderMapSettings({ fieldOfView: !fieldOfView })
                                    )
                                }
                            />
                            <h5>Field of View</h5>
                        </div>
                        <div className="flex gap-3 items-center">
                            <Checkbox
                                isChecked={flightPath}
                                onClick={() =>
                                    dispatch(setColorFinderMapSettings({ flightPath: !flightPath }))
                                }
                            />
                            <h5>Flight Path</h5>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2.5 py-1">
                        {mapBackgrounds.map((background) => (
                            <div
                                key={background.label}
                                className="flex gap-2 items-center cursor-pointer hover:bg-gray-700"
                                onClick={background.switchTo}
                            >
                                <RadioButton
                                    isSelected={background.isActiveBackground}
                                    onClick={background.switchTo}
                                />
                                <img
                                    src={`/images/maps/${background.label}.jpg`}
                                    alt={background.label}
                                    className={classNames(
                                        "overflow-hidden rounded-lg w-8 h-8 shadow-tooltip ml-1",
                                        {
                                            "border border-blue-500": background.isActiveBackground,
                                        }
                                    )}
                                />
                                <h5
                                    className={classNames({
                                        "text-gray-0": !isLight,
                                        "text-gray-100": isLight,
                                    })}
                                >
                                    {background.title}
                                </h5>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export { ColorFinderTopBar }
